/**
 * @architecture-header 1
 * @layer module
 * @domain live
 * @responsibility Show why a seat is flagged, how fresh that is, and how sure the system is.
 * @state-owner none; the seat record carries the signals
 * @allowed-imports React; shared view types; module alert state
 * @forbidden dataset mutations; direct API client
 * @public-api live run dashboard
 * @side-effects none
 * @tests modules/live/store/seat-alerts.test.ts and the workspace Playwright flows
 */

import type { SeatConfidence, SeatRecord } from '../../../shared/lib/model';
import { seatPresentation } from '../../../shared/lib/selectors';
import type { SeatAlert } from '../store/seat-alerts';

const CONFIDENCE_LABEL: Readonly<Record<SeatConfidence, string>> = {
  high: '判斷可靠',
  medium: '判斷普通',
  low: '判斷偏弱',
};

const ALERT_LABEL: Partial<Record<SeatAlert['state'], string>> = {
  ack: '已確認處理',
  snoozed: '已暫緩',
  ignored: '這堂課不再提醒',
  pinned: '已釘選',
};

export function SeatSignals({
  alert,
  seat,
  t,
}: {
  alert: SeatAlert;
  seat: SeatRecord;
  t: (source: string) => string;
}) {
  const flagged = seatPresentation(seat.status).needsTeacher;
  const parts: string[] = [];

  if (flagged && seat.reason) parts.push(t(seat.reason));
  if (flagged && seat.aiAttempts) parts.push(`${t('AI 已試')} ${seat.aiAttempts} ${t('種說法')}`);
  if (seat.repeatedConcept) parts.push(`${t('重複卡在')}${seat.repeatedConcept}`);
  /** Freshness last: everything above is only as current as this. */
  parts.push(
    seat.lastActiveMinutes <= 1
      ? t('剛剛還在作答')
      : `${t('最後動作')} ${seat.lastActiveMinutes} ${t('分鐘前')}`,
  );
  if (flagged) parts.push(t(CONFIDENCE_LABEL[seat.confidence]));

  const state = ALERT_LABEL[alert.state];

  return (
    <span className="sgline">
      {state ? <em className="sgtag">{t(state)}</em> : null}
      {parts.join(' · ')}
    </span>
  );
}
